// Comment boxes for the blueprint graphs (ENG-1119) — the pure half.
//
// A comment is a NODE, not a new kind of entity, and that choice is load-bearing:
// persistence, undo, copy/paste, collaboration and the autosave path are all
// already wired for nodes, and every one of them would need a parallel
// implementation for a sibling collection.
//
// It is INERT BY CONSTRUCTION, verified in both runtimes rather than assumed:
//   - `GraphEvaluator.evaluateGraph` walks BACKWARDS from the `final` edge, and
//     its only all-nodes pass sidebands `plugin.*` types, so an unconnected
//     comment is never visited.
//   - `EventRuntime.fireEvent` selects nodes by event TYPE, which a comment is
//     not.
// A comment therefore cannot change what a character does — it is documentation
// that travels with the graph.
//
// GROUPING: dragging a comment moves the nodes inside it. Membership is decided
// ONCE, at drag start (`nodesInside`), and never recomputed mid-drag — a node
// that leaves the rectangle while the mouse is moving would otherwise be
// dropped half-way and left behind, which reads as the drag "losing" nodes.
// Unreal behaves the same way.

// The colour list lives in components/nodes/commentTheme.js, not here: node
// colour is FUNCTIONAL (Unreal's convention — it encodes category, it must not
// follow the user's theme) and that directory is the palette lock's functional
// allowlist. A hex literal in this file fails tests/unit/paletteEnforcement.
import { COMMENT_COLORS, DEFAULT_COMMENT_COLOR } from '../components/nodes/commentTheme.js'

export { COMMENT_COLORS, DEFAULT_COMMENT_COLOR }

/** The node type string. One literal, shared by the editors and the tests. */
export const COMMENT_TYPE = 'comment'

/** Default box for a comment created without a drawn size. */
export const DEFAULT_COMMENT_SIZE = { width: 260, height: 140 }
/** Padding left around a selection when a comment is drawn around it. */
export const COMMENT_PADDING = { x: 24, top: 44, bottom: 20 }
/** Below this the box cannot be resized — a comment with no room for its text. */
export const MIN_COMMENT_SIZE = { width: 120, height: 70 }

/**
 * A node's size, from wherever this graph happens to record it. ReactFlow v12
 * measures into `measured`, an explicit size lands in `style`, and older saved
 * graphs carry bare `width`/`height`. Reading one only would silently treat a
 * node as zero-sized and put its centre in the wrong place.
 */
export function nodeSize(node) {
  const w = node?.width ?? node?.style?.width ?? node?.measured?.width ?? 0
  const h = node?.height ?? node?.style?.height ?? node?.measured?.height ?? 0
  return { width: Number(w) || 0, height: Number(h) || 0 }
}

/** A node's rectangle in flow coordinates. */
export function nodeRect(node) {
  const { width, height } = nodeSize(node)
  return { x: node?.position?.x ?? 0, y: node?.position?.y ?? 0, width, height }
}

/** A node's centre — what membership is decided on. */
export function nodeCentre(node) {
  const r = nodeRect(node)
  return { x: r.x + r.width / 2, y: r.y + r.height / 2 }
}

/**
 * Build a comment node.
 *
 * `id` is supplied by the caller because each editor mints ids from its own
 * live graph (see nodeClipboard.js's note on why a shared counter drifts).
 */
export function createComment({ id, position, width, height, text = '', color = DEFAULT_COMMENT_COLOR }) {
  return {
    id,
    type: COMMENT_TYPE,
    position: { x: position?.x ?? 0, y: position?.y ?? 0 },
    // TOP-LEVEL width/height, not `style`. xyflow's `nodeHasDimensions` reads
    // `measured ?? width ?? initialWidth` and never looks at style, so a node
    // sized only through style counts as unmeasured and the wrapper is rendered
    // `visibility: hidden` — the comment existed, occupied the right rectangle,
    // and was invisible AND unhittable. Measured in the running editor.
    // NodeResizer writes these same two fields, so a resize round-trips.
    width: Math.max(MIN_COMMENT_SIZE.width, width ?? DEFAULT_COMMENT_SIZE.width),
    height: Math.max(MIN_COMMENT_SIZE.height, height ?? DEFAULT_COMMENT_SIZE.height),
    data: { text, color },
    // ZERO, NOT -1. Measured in the running editor: ReactFlow renders every
    // node at `z-index: 0` inside `.react-flow__pane`, which is their PARENT at
    // `z-index: 1`. A child at -1 therefore paints behind its own parent and the
    // pane swallows every click on it — the comment was there, visible, and
    // completely dead to the pointer. Depth comes from array ORDER instead
    // (see `withCommentBehind`), which is what decides paint order at equal z.
    zIndex: 0,
    // Never a connection target/source — it has no pins at all.
    selectable: true,
    deletable: true,
  }
}

/**
 * Normalise a drawn rectangle: a drag may go up and/or left, which yields a
 * negative width or height if taken literally.
 */
export function rectFromDrag(start, end) {
  const x = Math.min(start.x, end.x)
  const y = Math.min(start.y, end.y)
  return {
    x, y,
    width: Math.abs(end.x - start.x),
    height: Math.abs(end.y - start.y),
  }
}

/** Whether a drawn rectangle is big enough to be a deliberate box, not a stray click. */
export function isDeliberateDraw(rect, min = 24) {
  return (rect?.width ?? 0) >= min && (rect?.height ?? 0) >= min
}

/**
 * The rectangle enclosing `nodes`, with room at the top for the comment's own
 * title. Returns null for an empty selection — the caller falls back to a
 * default-size box rather than a zero-sized one.
 */
export function boundsAroundNodes(nodes, pad = COMMENT_PADDING) {
  const rects = (nodes ?? []).map(nodeRect).filter((r) => r.width > 0 || r.height > 0)
  if (rects.length === 0) return null
  const minX = Math.min(...rects.map((r) => r.x))
  const minY = Math.min(...rects.map((r) => r.y))
  const maxX = Math.max(...rects.map((r) => r.x + r.width))
  const maxY = Math.max(...rects.map((r) => r.y + r.height))
  return {
    x: minX - pad.x,
    y: minY - pad.top,
    width: (maxX - minX) + pad.x * 2,
    height: (maxY - minY) + pad.top + pad.bottom,
  }
}

/**
 * Ids of the nodes the comment FULLY ENCLOSES.
 *
 * Unreal's rule, read from the source rather than remembered: its
 * SGraphNodeComment::IsNodeUnderComment ends in
 * `FSlateRect::IsRectangleContained(CommentRect, NodeRect)`, which is
 * `A.Left <= B.Left && A.Right >= B.Right && ...` — the whole node inside the
 * whole comment. Its own property is even called bMoveAnyEnclosedNodes.
 *
 * This file previously used the node's CENTRE and claimed that was Unreal's
 * rule. It is not. Centre is friendlier at the edges, but a comment that drags
 * a node hanging half outside it is a surprise, and matching the editor people
 * already know beats a private improvement.
 *
 * Other comments are excluded: dragging one comment must not drag another it
 * overlaps, which would cascade.
 */
export function nodesInside(comment, nodes) {
  const r = nodeRect(comment)
  if (r.width <= 0 || r.height <= 0) return []
  return (nodes ?? [])
    .filter((n) => n && n.id !== comment?.id && n.type !== COMMENT_TYPE)
    .filter((n) => {
      const b = nodeRect(n)
      // A zero-sized node (not yet measured) would pass a containment test at
      // any position it happens to sit; it is not enclosed, it is unknown.
      if (b.width <= 0 || b.height <= 0) return false
      return r.x <= b.x && r.x + r.width >= b.x + b.width
        && r.y <= b.y && r.y + r.height >= b.y + b.height
    })
    .map((n) => n.id)
}

/**
 * Apply a drag delta to the captured members.
 *
 * The comment itself is NOT moved here — ReactFlow already moved it; doing it
 * again would double the distance.
 */
export function moveMembers(nodes, memberIds, delta) {
  const ids = new Set(memberIds ?? [])
  if (ids.size === 0 || (!delta?.x && !delta?.y)) return nodes
  return (nodes ?? []).map((n) => (
    ids.has(n.id)
      ? { ...n, position: { x: n.position.x + (delta.x ?? 0), y: n.position.y + (delta.y ?? 0) } }
      : n
  ))
}

/**
 * Put a comment into the node list so it renders BEHIND the nodes.
 *
 * Equal z-index means DOM order decides, and ReactFlow renders in array order,
 * so a backdrop has to go FIRST. Appending would paint the comment over the
 * nodes it is supposed to sit behind — and a large one would then cover them.
 */
export function withCommentBehind(nodes, comment) {
  return [comment, ...(nodes ?? [])]
}

// ── "type immediately", Unreal's RequestRenameOnSpawn ────────────────────────
//
// SGraphPanel::AddNode calls NewNode->RequestRenameOnSpawn() for every
// user-added node, and SGraphNode's default implementation calls
// RequestRename() — the comment node does not override it, so a comment you
// just made is already in text entry. Ours needed a double-click first, which
// is a second gesture for something you only ever create in order to write in.
//
// A MODULE SINGLETON rather than a field on the node: an `autoEdit` flag in
// `data` would be persisted with the graph and re-opened on every load, and
// clearing it would mean writing to the graph from a mount effect. This is
// transient UI intent, so it lives outside the document.
//
// THE READ IS PURE, and that is not a style preference. The first version
// consumed the flag inside `useState(() => claim(id))` — and React.StrictMode
// (on, in main.jsx) invokes a state initializer TWICE, so the claim was spent
// on the render React then throws away and the surviving render saw `false`.
// The comment appeared and did not open for typing. So: `isCommentPendingEdit`
// only looks, and `releaseCommentEdit` is called from a real user action (the
// first edit or the blur), which is also what stops a later remount reopening
// the editor over whatever someone is doing by then.
let pendingEditId = null

/** Mark a just-created comment as the one that should open in text entry. */
export function markCommentForEdit(id) {
  pendingEditId = id ?? null
}

/** Whether this comment should open in text entry. PURE — safe to call in render. */
export function isCommentPendingEdit(id) {
  return !!id && pendingEditId === id
}

/** Give up the pending edit. Called once the user has actually typed or left. */
export function releaseCommentEdit(id) {
  if (!id || pendingEditId === id) pendingEditId = null
}

/** Test seam. */
export function _pendingCommentEdit() { return pendingEditId }
